import { useState } from 'react';
import type { FacetOption, Filters } from '../lib/types';

interface FilterRowProps {
  label: string;
  /** Small muted note beside the label, e.g. to signal multi-select. */
  hint?: string;
  options: FacetOption[];
  /** Whether a given option is currently on. */
  isSelected: (key: string) => boolean;
  /** Whether anything at all is selected in this row (drives the "All" pill). */
  anySelected: boolean;
  /** `null` means the "All" pill — clear this row. */
  onSelect: (value: string | null) => void;
}

/**
 * One row of filter pills. Single- and multi-select rows share this component;
 * the parent decides the semantics by what it passes for `isSelected` and
 * `onSelect`. `aria-pressed` is correct for both cases.
 */
function FilterRow({ label, hint, options, isSelected, anySelected, onSelect }: FilterRowProps) {
  if (options.length === 0) return null;

  const groupId = `filter-${label.toLowerCase().replace(/[^a-z]+/g, '-')}`;

  return (
    <div className="filter-row">
      <span className="filter-row__label" id={groupId}>
        {label}
        {hint && <span className="filter-row__hint"> {hint}</span>}
      </span>
      <div className="filter-row__pills" role="group" aria-labelledby={groupId}>
        <button
          type="button"
          className={`pill${!anySelected ? ' pill--active' : ''}`}
          aria-pressed={!anySelected}
          onClick={() => onSelect(null)}
        >
          All
        </button>
        {options.map((option) => (
          <button
            key={option.key}
            type="button"
            className={`pill${isSelected(option.key) ? ' pill--active' : ''}`}
            aria-pressed={isSelected(option.key)}
            onClick={() => onSelect(option.key)}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Free-text search.
 *
 * Sits outside the collapsible body on purpose: on a phone the filter rows are
 * hidden behind a toggle, and a search box you have to open a panel to reach is
 * one nobody uses. `type="search"` gets the on-screen keyboard's Search key and
 * the browser's own clear affordance; the explicit button is there because
 * Firefox does not draw one.
 */
function SearchBox({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  return (
    <div className="search">
      <svg className="search__icon" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
        <circle cx="11" cy="11" r="7" fill="none" stroke="currentColor" strokeWidth="2" />
        <path d="M16.5 16.5 21 21" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
      <input
        id="group-search"
        className="search__input"
        type="search"
        value={value}
        placeholder="Search groups, leaders, topics…"
        aria-label="Search groups by name, leader, day, or topic"
        onChange={(event) => onChange(event.target.value)}
      />
      {value && (
        <button
          type="button"
          className="search__clear"
          aria-label="Clear search"
          onClick={() => onChange('')}
        >
          <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
            <path
              d="M6 6l12 12M18 6L6 18"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
            />
          </svg>
        </button>
      )}
    </div>
  );
}

interface FilterBarProps {
  facets: { locations: FacetOption[]; days: FacetOption[]; audiences: FacetOption[] };
  filters: Filters;
  onToggle: (key: 'location' | 'audience', value: string | null) => void;
  onToggleDay: (value: string | null) => void;
  onKidsMinistry: (value: boolean) => void;
  onQuery: (value: string) => void;
  onClear: () => void;
  activeCount: number;
  resultCount: number;
}

export function FilterBar({
  facets,
  filters,
  onToggle,
  onToggleDay,
  onKidsMinistry,
  onQuery,
  onClear,
  activeCount,
  resultCount,
}: FilterBarProps) {
  /*
   * On a phone the four filter rows run past 800px, which would push every
   * group below the fold — exactly wrong for the case this page exists to
   * serve: someone tapping a pre-filtered link and scanning the results. So on
   * small screens the rows collapse behind a toggle while the result count
   * stays visible. At >=640px the rows are always shown and the toggle is
   * hidden (see app.css).
   */
  const [open, setOpen] = useState(false);

  return (
    <section className="filters" aria-label="Filter groups">
      {/* First in the DOM so it is the first thing reached by keyboard and by
          a screen reader. On desktop CSS reorders the count row below it. */}
      <SearchBox value={filters.query} onChange={onQuery} />

      <div className="filters__bar">
        <button
          type="button"
          className="filters__toggle"
          aria-expanded={open}
          aria-controls="filter-body"
          onClick={() => setOpen((value) => !value)}
        >
          <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
            <path
              d="M3 6h18M6 12h12M10 18h4"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
          Filters
          {activeCount > 0 && <span className="filters__badge">{activeCount}</span>}
        </button>

        {/* Result count is always visible, expanded or collapsed. */}
        <p className="result-count" role="status" aria-live="polite">
          <strong>{resultCount}</strong> {resultCount === 1 ? 'group' : 'groups'}
        </p>

        {activeCount > 0 && (
          <button type="button" className="link-button" onClick={onClear}>
            Clear filters
          </button>
        )}
      </div>

      <div className="filters__body" id="filter-body" data-open={open}>
        {/* "Location", not "Campus": the options are the campuses plus Homes. */}
        <FilterRow
          label="Location"
          options={facets.locations}
          isSelected={(key) => filters.location === key}
          anySelected={Boolean(filters.location)}
          onSelect={(value) => onToggle('location', value)}
        />

        {/* Multi-select — the hint is there because stacked active pills are
            not otherwise an obvious affordance. */}
        <FilterRow
          label="Day"
          hint="pick any"
          options={facets.days}
          isSelected={(key) => filters.days.includes(key)}
          anySelected={filters.days.length > 0}
          onSelect={onToggleDay}
        />

        <FilterRow
          label="Who it's for"
          options={facets.audiences}
          isSelected={(key) => filters.audience === key}
          anySelected={Boolean(filters.audience)}
          onSelect={(value) => onToggle('audience', value)}
        />

        {/* CCB calls this "childcare"; Crossroads calls it Kids Ministry, and
            the Kids Ministry team cares about the distinction. */}
        <div className="filter-row">
          <span className="filter-row__label" id="filter-kids">
            Kids Ministry
          </span>
          <div className="filter-row__pills" role="group" aria-labelledby="filter-kids">
            <button
              type="button"
              className={`pill${!filters.kidsMinistry ? ' pill--active' : ''}`}
              aria-pressed={!filters.kidsMinistry}
              onClick={() => onKidsMinistry(false)}
            >
              All
            </button>
            <button
              type="button"
              className={`pill${filters.kidsMinistry ? ' pill--active' : ''}`}
              aria-pressed={filters.kidsMinistry}
              onClick={() => onKidsMinistry(true)}
            >
              Kids Ministry provided
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
